import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  useAuditList, 
  useAuditStats, 
  useRegisterAudit, 
  useMarkAuditFailed,
  AuditListFilters,
  AuditListItem,
  AuditStatus,
} from '../../../hooks/useTemplateAudit';
import { AuditStatusBadge } from '../../../components/smart-contracts';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { 
  Shield, 
  ShieldCheck, 
  ShieldX, 
  ShieldAlert,
  Search,
  Filter,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  FileCode,
  Calendar,
  Building2,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';

const AUDIT_STATUS_OPTIONS: { value: AuditStatus | ''; label: string }[] = [
  { value: '', label: 'Todos os status' },
  { value: 'PENDING_AUDIT', label: 'Pendente' },
  { value: 'AUDITED', label: 'Auditado' },
  { value: 'AUDIT_FAILED', label: 'Falha' },
];

export function AuditListPage() {
  const navigate = useNavigate();
  
  // Filters state
  const [filters, setFilters] = useState<AuditListFilters>({
    page: 1,
    limit: 10,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });
  
  // Modal state
  const [selectedTemplate, setSelectedTemplate] = useState<AuditListItem | null>(null);
  const [modalMode, setModalMode] = useState<'register' | 'fail' | null>(null);
  
  // Form state for register audit
  const [auditorCompany, setAuditorCompany] = useState('');
  const [auditDate, setAuditDate] = useState('');
  const [reportUrl, setReportUrl] = useState('');
  const [findings, setFindings] = useState('');
  
  // Form state for fail audit
  const [failReason, setFailReason] = useState('');
  const [failAuditor, setFailAuditor] = useState('');
  
  const [errors, setErrors] = useState<string[]>([]);
  
  // Queries
  const { data: auditList, isLoading: isLoadingList } = useAuditList(filters);
  const { data: stats, isLoading: isLoadingStats } = useAuditStats();
  
  // Mutations
  const registerAuditMutation = useRegisterAudit();
  const markFailedMutation = useMarkAuditFailed();
  
  const handleFilterChange = (key: keyof AuditListFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      page: key !== 'page' ? 1 : value, // Reset page when other filters change
    }));
  };
  
  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };
  
  const openRegisterModal = (template: AuditListItem) => {
    setSelectedTemplate(template);
    setModalMode('register');
    setAuditorCompany('');
    setAuditDate('');
    setReportUrl('');
    setFindings('');
    setErrors([]);
  };
  
  const openFailModal = (template: AuditListItem) => {
    setSelectedTemplate(template);
    setModalMode('fail');
    setFailReason('');
    setFailAuditor('');
    setErrors([]);
  };
  
  const closeModal = () => {
    setSelectedTemplate(null);
    setModalMode(null);
    setErrors([]);
  };

  const handleRegisterAudit = async () => {
    const newErrors: string[] = [];
    if (!auditorCompany.trim()) newErrors.push('Empresa auditora é obrigatória');
    if (!auditDate) newErrors.push('Data da auditoria é obrigatória');
    if (!reportUrl.trim()) newErrors.push('URL do relatório é obrigatória');
    
    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }
    
    try {
      await registerAuditMutation.mutateAsync({
        templateId: selectedTemplate!.id,
        dto: {
          auditorCompany: auditorCompany.trim(),
          auditDate,
          reportUrl: reportUrl.trim(),
          findings: findings.trim() || undefined,
        },
      });
      closeModal();
    } catch (error) {
      setErrors(['Erro ao registrar auditoria. Tente novamente.']);
    }
  };
  
  const handleMarkFailed = async () => {
    if (!failReason.trim()) {
      setErrors(['Motivo da falha é obrigatório']);
      return;
    }
    
    try {
      await markFailedMutation.mutateAsync({
        templateId: selectedTemplate!.id,
        dto: {
          reason: failReason.trim(),
          auditorCompany: failAuditor.trim() || undefined,
        },
      });
      closeModal();
    } catch (error) {
      setErrors(['Erro ao marcar falha. Tente novamente.']);
    }
  };
  
  const getStatusIcon = (status: AuditStatus) => {
    switch (status) {
      case 'AUDITED':
        return <ShieldCheck className="w-5 h-5 text-green-400" />;
      case 'AUDIT_FAILED':
        return <ShieldX className="w-5 h-5 text-red-400" />;
      default:
        return <ShieldAlert className="w-5 h-5 text-yellow-400" />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Auditorias de Smart Contracts</h1>
          <p className="text-surface-400">Gerencie as auditorias de todos os templates</p>
        </div>
      </div>
      
      {/* Stats Cards */}
      {!isLoadingStats && stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-surface-800 rounded-lg border border-surface-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-surface-700 rounded-lg">
                <FileCode className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Total Templates</p>
                <p className="text-2xl font-bold text-surface-100">{stats.totalTemplates}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-surface-800 rounded-lg border border-surface-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <ShieldAlert className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-400">{stats.byStatus.PENDING_AUDIT}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-surface-800 rounded-lg border border-surface-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Auditados</p>
                <p className="text-2xl font-bold text-green-400">{stats.byStatus.AUDITED}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-surface-800 rounded-lg border border-surface-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <ShieldX className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Falhas</p>
                <p className="text-2xl font-bold text-red-400">{stats.byStatus.AUDIT_FAILED}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-surface-800 rounded-lg border border-surface-700 p-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          {/* Status */}
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value as AuditStatus | '')}
            className="w-40 px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {AUDIT_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          
          {/* Auditor Company */}
          <div className="relative w-48">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
            <input
              type="text"
              placeholder="Empresa auditora..."
              value={filters.auditorCompany || ''}
              onChange={(e) => handleFilterChange('auditorCompany', e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          {/* Date Range */}
          <input
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="w-36 px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <input
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="w-36 px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      
      {/* Table */}
      <div className="bg-surface-800 rounded-lg border border-surface-700 overflow-hidden">
        {isLoadingList ? (
          <div className="p-8 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : auditList && auditList.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Template</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Padrão</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Empresa</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Data</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-surface-400">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-700">
                  {auditList.data.map((item) => (
                    <tr key={item.id} className="hover:bg-surface-700/50 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/admin/tokenization/contracts/${item.id}`)}
                          className="text-left hover:text-primary-400 transition-colors"
                        >
                          <p className="font-medium text-surface-100">{item.name}</p>
                          <p className="text-sm text-surface-500">v{item.currentVersion}</p>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{item.tokenStandard}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <AuditStatusBadge status={item.auditStatus as AuditStatus} />
                      </td>
                      <td className="px-4 py-3 text-surface-300">
                        {item.auditorCompany || '-'}
                      </td>
                      <td className="px-4 py-3 text-surface-300">
                        {item.auditDate 
                          ? new Date(item.auditDate).toLocaleDateString('pt-BR')
                          : '-'
                        }
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {item.auditReportUrl && (
                            <a
                              href={item.auditReportUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 text-surface-400 hover:text-primary-400 transition-colors"
                              title="Ver relatório"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          {item.auditStatus === 'PENDING_AUDIT' && (
                            <>
                              <Button size="sm" onClick={() => openRegisterModal(item)}>
                                <ShieldCheck className="w-4 h-4 mr-1" />
                                Registrar
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openFailModal(item)}>
                                <ShieldX className="w-4 h-4 mr-1" />
                                Falha
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-surface-700 flex items-center justify-between">
              <p className="text-sm text-surface-400">
                Mostrando {((filters.page || 1) - 1) * (filters.limit || 10) + 1} a{' '}
                {Math.min((filters.page || 1) * (filters.limit || 10), auditList.meta.total)} de{' '}
                {auditList.meta.total} resultados
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={(filters.page || 1) <= 1}
                  onClick={() => handlePageChange((filters.page || 1) - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-surface-300">
                  Página {filters.page || 1} de {auditList.meta.totalPages}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={(filters.page || 1) >= auditList.meta.totalPages}
                  onClick={() => handlePageChange((filters.page || 1) + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-surface-400">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma auditoria encontrada</p>
          </div>
        )}
      </div>
      
      {/* Register Audit Modal */}
      {modalMode === 'register' && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-800 rounded-lg border border-surface-700 w-full max-w-lg mx-4">
            <div className="px-4 py-3 border-b border-surface-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-green-400" />
                <h2 className="font-medium text-surface-100">Registrar Auditoria</h2>
              </div>
              <button onClick={closeModal} className="text-surface-400 hover:text-surface-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-surface-300">
                Template: <span className="font-medium text-surface-100">{selectedTemplate.name}</span>
              </p>
              
              {errors.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-400 mb-1">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Corrija os erros:</span>
                  </div>
                  <ul className="list-disc list-inside text-red-300 text-sm">
                    {errors.map((error, i) => <li key={i}>{error}</li>)}
                  </ul>
                </div>
              )}
              
              <div>
                <label className="block text-sm text-surface-400 mb-1">Empresa Auditora *</label>
                <input
                  type="text"
                  value={auditorCompany}
                  onChange={(e) => setAuditorCompany(e.target.value)}
                  placeholder="Ex: CertiK, OpenZeppelin"
                  className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm text-surface-400 mb-1">Data da Auditoria *</label>
                <input
                  type="date"
                  value={auditDate}
                  onChange={(e) => setAuditDate(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm text-surface-400 mb-1">URL do Relatório *</label>
                <input
                  type="url"
                  value={reportUrl}
                  onChange={(e) => setReportUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm text-surface-400 mb-1">Observações</label>
                <textarea
                  value={findings}
                  onChange={(e) => setFindings(e.target.value)}
                  placeholder="Resumo dos achados..."
                  rows={3}
                  className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>
            </div>
            
            <div className="px-4 py-3 border-t border-surface-700 flex justify-end gap-2">
              <Button variant="ghost" onClick={closeModal}>Cancelar</Button>
              <Button onClick={handleRegisterAudit} disabled={registerAuditMutation.isPending}>
                <CheckCircle className="w-4 h-4 mr-2" />
                {registerAuditMutation.isPending ? 'Salvando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Failed Modal */}
      {modalMode === 'fail' && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-800 rounded-lg border border-surface-700 w-full max-w-lg mx-4">
            <div className="px-4 py-3 border-b border-surface-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldX className="w-5 h-5 text-red-400" />
                <h2 className="font-medium text-surface-100">Marcar Auditoria como Falha</h2>
              </div>
              <button onClick={closeModal} className="text-surface-400 hover:text-surface-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-surface-300">
                Template: <span className="font-medium text-surface-100">{selectedTemplate.name}</span>
              </p>
              
              {errors.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-400 mb-1">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Corrija os erros:</span>
                  </div>
                  <ul className="list-disc list-inside text-red-300 text-sm">
                    {errors.map((error, i) => <li key={i}>{error}</li>)}
                  </ul>
                </div>
              )}
              
              <div>
                <label className="block text-sm text-surface-400 mb-1">Empresa Auditora</label>
                <input
                  type="text"
                  value={failAuditor}
                  onChange={(e) => setFailAuditor(e.target.value)}
                  placeholder="Ex: CertiK"
                  className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm text-surface-400 mb-1">Motivo da Falha *</label>
                <textarea
                  value={failReason}
                  onChange={(e) => setFailReason(e.target.value)}
                  placeholder="Descreva os problemas encontrados..."
                  rows={4}
                  className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>
            </div>
            
            <div className="px-4 py-3 border-t border-surface-700 flex justify-end gap-2">
              <Button variant="ghost" onClick={closeModal}>Cancelar</Button>
              <Button variant="outline" onClick={handleMarkFailed} disabled={markFailedMutation.isPending}>
                <ShieldX className="w-4 h-4 mr-2" />
                {markFailedMutation.isPending ? 'Salvando...' : 'Confirmar Falha'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditListPage;
